import { FormEvent, useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { supabase } from "./lib/supabase";
import { StandMediaSettings } from "./StandMediaSettings";
import { StandInstitutionalMedia } from "./StandInstitutionalMedia";
import { SiteAdvancedSettings } from "./SiteAdvancedSettings";
import { DirectPageCanvas } from "./DirectPageCanvas";
import { SiteTemplatePreview } from "./SiteTemplatePreview";
import { StandSitePreviewModal } from "./StandSitePreviewModal";
import { StandPublicVehicleSettings, defaultPublicVehicleSettings } from "./StandPublicVehicleSettings";
import type { PublicVehicleSettings } from "./StandPublicVehicleSettings";
import { ThemeBrand, ThemeFooter, ThemeNav, type TemplateStand } from "./StandHomeTemplate";
import type { UnifiedPublicSiteSnapshot } from "./public-site-contract";
import "./stand-site-builder.css";
import "./stand-site-builder-refinement.css";

type Template = {
  id: string;
  name: string;
  description: string;
  version_id: string;
  version_number: number;
  config: Record<string, unknown>;
};
type Block = { id: string; type: string; content: string };
type PagePreset = "basic" | "offers" | "promotions" | "about" | "contacts";
type Page = {
  slug: string;
  title: string;
  body: string;
  status?: "draft" | "published" | "archived";
  blocks?: Block[];
  editor_version?: number;
  seo?: {
    title: string;
    description: string;
    index: boolean;
    canonical: string;
  };
};
type Content = {
  identity: { name: string; tagline: string };
  hero: { headline: string; intro: string; button: string };
  colors: { accent: string };
  seo: { title: string; description: string };
  pages: Page[];
};
type Revision = {
  id: string;
  revision_number: number;
  note: string;
  created_at: string;
};
type Site = {
  template_version_id: string;
  draft_content: Content;
  published_revision_id: string | null;
  enabled: boolean;
  custom_domain: string | null;
  domain_status: string;
  domain_verification_token: string;
  visual_settings?: any;
  updated_at: string;
};
type Builder = {
  site: Site;
  templates: Template[];
  revisions: Revision[];
  platform_url: string;
};
const emptyPage: Page = {
  slug: "",
  title: "",
  body: "",
  status: "draft",
  blocks: [],
  seo: { title: "", description: "", index: true, canonical: "" },
};
const blockTypes = [
  ["header", "Cabeçalho"], ["footer", "Rodapé"],
  ["heading", "Título"], ["text", "Texto"], ["image", "Imagem"],
  ["gallery", "Galeria"], ["video", "Vídeo"], ["cta", "Botão"],
  ["form", "Formulário de contacto"], ["divider", "Separador"], ["columns", "Colunas"],
  ["map", "Mapa"], ["vehicles", "Viaturas"], ["testimonials", "Testemunhos"],
  ["faq", "Perguntas"], ["services", "Serviços"],
] as const;
// O painel “Adicionar elemento” reúne os blocos; serviços apresenta automaticamente os serviços ativos configurados em Geral.
// A opção de indexação explica que motores de pesquisa podem incluir esta página nos resultados.
const pagePresets: { key: PagePreset; name: string; description: string }[] = [
  { key: "basic", name: "Página básica", description: "Cabeçalho e rodapé do template, com o conteúdo central vazio." },
  { key: "offers", name: "Ofertas", description: "Estrutura pronta para apresentar oportunidades e viaturas em destaque." },
  { key: "promotions", name: "Promoções", description: "Apresentação, condições, regras, validade e chamada para ação." },
  { key: "about", name: "Sobre nós", description: "Apresentação do stand, história, equipa e valores." },
  { key: "contacts", name: "Contactos", description: "Informação de contacto, localização, horário e formulário." },
];
const makeBlock = (type: string, content = ""): Block => ({ id: crypto.randomUUID(), type, content });
const pageChromeBlocks = (): Block[] => [makeBlock("header"), makeBlock("footer")];
const presetBlocks = (preset: PagePreset): Block[] => {
  const [header, footer] = pageChromeBlocks();
  if (preset === "offers") return [header, makeBlock("heading", "Ofertas"), makeBlock("text", "Apresente aqui as ofertas disponíveis."), makeBlock("vehicles"), footer];
  if (preset === "promotions") return [header, makeBlock("heading", "Promoções"), makeBlock("text", "Apresente aqui a promoção."), makeBlock("heading", "Condições, regras e validade"), makeBlock("text", "Indique todas as condições aplicáveis à promoção."), makeBlock("cta", "Saber mais"), footer];
  if (preset === "about") return [header, makeBlock("heading", "Sobre nós"), makeBlock("text", "Conte a história do stand e apresente a sua equipa e os seus valores."), footer];
  if (preset === "contacts") return [header, makeBlock("heading", "Contactos"), makeBlock("text", "Indique como e quando os visitantes podem contactar o stand."), makeBlock("map"), makeBlock("form", "Fale connosco"), footer];
  return [header, footer];
};
const normalizePage = (page: Page): Page => {
  if ((page.editor_version ?? 0) >= 2) return page;
  const legacyBlocks = page.blocks ?? (page.body ? [makeBlock("text", page.body)] : []);
  const [header, footer] = pageChromeBlocks();
  return { ...page, editor_version: 2, blocks: [header, ...legacyBlocks, footer] };
};
const slugifyPageName = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function EditorTemplateHeader({layout,stand}:{layout:string;stand:TemplateStand}) {
  if(layout==="atelier") return <><div className="atelier-note"><span>{stand.email||"Automóveis escolhidos com critério"}</span></div><header className="atelier-header"><ThemeNav stand={stand}/><ThemeBrand stand={stand}/><div className="theme-header-actions"><a href="#contactos">Marcar visita</a></div></header></>;
  if(layout==="horizonte") return <header className="horizonte-editor-header"><ThemeBrand stand={stand}/><ThemeNav stand={stand}/></header>;
  return <header className={`${layout}-header`}><ThemeBrand stand={stand}/><ThemeNav stand={stand}/><div className="theme-header-actions"><a href="#contactos">Contactar</a></div></header>;
}

export function StandSiteBuilder({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const [data, setData] = useState<Builder | null>(null),
    [content, setContent] = useState<Content | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [saving, setSaving] = useState(false),
    [domain, setDomain] = useState(""),
    [templateVersionId, setTemplateVersionId] = useState(""),
    [previewOpen, setPreviewOpen] = useState(false),
    [previewTemplateVersionId, setPreviewTemplateVersionId] = useState<string | null>(null),
    [previewSnapshot, setPreviewSnapshot] = useState<UnifiedPublicSiteSnapshot<Record<string, unknown>> | null>(null),
    [activePageIndex, setActivePageIndex] = useState(0),
    [activeBlockId, setActiveBlockId] = useState<string | null>(null),
    [editorStudioOpen, setEditorStudioOpen] = useState(false),
    [editorLeftOpen, setEditorLeftOpen] = useState(false),
    [editorLeftTab, setEditorLeftTab] = useState<"blocks"|"patterns"|"media">("blocks"),
    [editorRightTab, setEditorRightTab] = useState<"page"|"block">("page"),
    [editorSearch, setEditorSearch] = useState(""),
    [newPageName, setNewPageName] = useState(""),
    [newPagePreset, setNewPagePreset] = useState<PagePreset>("basic"),
    [institutionalProfile, setInstitutionalProfile] = useState<TemplateStand["profile"]>(),
    [publicServices, setPublicServices] = useState<TemplateStand["services"]>([]),
    [publicMetrics,setPublicMetrics]=useState<TemplateStand["metrics"]>([]),
    [publicCredit,setPublicCredit]=useState<TemplateStand["credit"]>({public_financing_enabled:false});
  const [publicVehicleSettings,setPublicVehicleSettings]=useState<PublicVehicleSettings>(defaultPublicVehicleSettings);
  const [publicFinancing,setPublicFinancing]=useState<TemplateStand["financing"]>({public_financing_enabled:false,simulator_enabled:false,minimum_term_months:12,maximum_term_months:84,term_step_months:12,minimum_down_payment_percent:0,headline:"Financiamento",introduction:"Peça o contacto da nossa equipa.",disclosure:"Simulação meramente indicativa, não vinculativa e sujeita a análise e aprovação."});
  const [institutionalMedia,setInstitutionalMedia]=useState<TemplateStand["media"]>({});
  const [publicReputation,setPublicReputation]=useState<TemplateStand["reputation"]>({});
  const [publicNewsletter,setPublicNewsletter]=useState<TemplateStand["newsletter"]>();
  const load = async () => {
    const { data: result, error } = await supabase.rpc("get_site_builder", {
      p_organization_id: organizationId,
    });
    if (error) {
      setNotice(error.message);
      return;
    }
    const next = result as Builder;
    setData(next);
    setContent({ ...next.site.draft_content, pages: next.site.draft_content.pages.map(normalizePage) });
    setTemplateVersionId(next.site.template_version_id);
    setDomain(next.site.custom_domain ?? "");
    const { data: general } = await supabase
      .from("organization_settings")
      .select("settings")
      .eq("organization_id", organizationId)
      .single();
    const generalSettings = general?.settings as { stand?: TemplateStand["profile"] & { slogan?:string }; public_services?:TemplateStand["services"] } | null;
    setInstitutionalProfile(generalSettings?.stand);
    setPublicServices((generalSettings?.public_services??[]).filter(item=>item.active!==false));
    const capabilityResults=await Promise.all([supabase.rpc("read_stand_public_metrics",{p_organization_id:organizationId}),supabase.rpc("read_stand_credit_intermediation",{p_organization_id:organizationId}),supabase.rpc("read_stand_institutional_media",{p_organization_id:organizationId}),supabase.rpc("read_stand_public_reputation",{p_organization_id:organizationId}),supabase.rpc("read_stand_public_financing_settings",{p_organization_id:organizationId}),supabase.rpc("read_stand_newsletter_settings",{p_organization_id:organizationId})]);
    const [metrics,credit,media,reputation,financing,newsletter]=capabilityResults.map((result)=>result.data);
    setPublicMetrics((metrics??[]) as TemplateStand["metrics"]);
    setPublicCredit((credit??{public_financing_enabled:false}) as TemplateStand["credit"]);
    setInstitutionalMedia((media??{}) as TemplateStand["media"]);
    setPublicReputation((reputation??{}) as TemplateStand["reputation"]);
    setPublicFinancing(({...(financing??{}),public_financing_enabled:Boolean(financing?.legal_enabled&&financing?.module_enabled)}) as TemplateStand["financing"]);
    setPublicNewsletter((newsletter??undefined) as TemplateStand["newsletter"]);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  const update = (area: keyof Content, key: string, value: unknown) =>
    setContent((current) =>
      current
        ? { ...current, [area]: { ...(current[area] as object), [key]: value } }
        : current,
    );
  const saveDraft = async () => {
    if (!data || !content) return;
    setSaving(true);
    setNotice("A guardar…");
    const { error } = await supabase.rpc("save_site_draft", {
      p_organization_id: organizationId,
      p_template_version_id: templateVersionId,
      p_content: content,
    });
    setSaving(false);
    setNotice(error?.message ?? "Site guardado.");
    if (!error) await load();
  };
  const openPreview = async (candidateVersionId?: string) => {
    if (!data || !content) return;
    setSaving(true);
    setPreviewTemplateVersionId(candidateVersionId ?? templateVersionId);
    setNotice("A preparar a pré-visualização integral…");
    const { data: snapshot, error } = await supabase.rpc("preview_public_stand_site", { p_organization_id: organizationId });
    setSaving(false);
    if (error || !snapshot) {
      setPreviewSnapshot(null);
      setPreviewOpen(true);
      setNotice("Pré-visualização local preparada. O contrato público integral ficará disponível na primeira publicação.");
      return;
    }
    setPreviewSnapshot(snapshot as UnifiedPublicSiteSnapshot<Record<string, unknown>>);
    setPreviewOpen(true);
    setNotice("Pré-visualização preparada com o mesmo contrato do site público.");
  };
  const chooseTemplate = (versionId: string) => {
    if (versionId === templateVersionId) return;
    setTemplateVersionId(versionId);
    setNotice("Template selecionado. Pode agora pré-visualizar, guardar ou publicar.");
  };
  const publish = async () => {
    if (!data || !content) return false;
    setSaving(true);
    setNotice("A publicar…");
    const draft = await supabase.rpc("save_site_draft", {
      p_organization_id: organizationId,
      p_template_version_id: templateVersionId,
      p_content: content,
    });
    if (draft.error) {
      setSaving(false);
      setNotice(draft.error.message);
      return false;
    }
    const { error } = await supabase.rpc("publish_site", {
      p_organization_id: organizationId,
      p_note: "Publicação pelo editor do stand",
    });
    setSaving(false);
    setNotice(error?.message ?? "Site publicado.");
    if (!error) await load();
    return !error;
  };
  const saveDomain = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const { data: result, error } = await supabase.rpc("save_site_domain", {
      p_organization_id: organizationId,
      p_domain: domain,
    });
    setSaving(false);
    setNotice(
      error?.message ??
        (result?.status === "pending_dns"
          ? `Domínio guardado. Crie o registo TXT ${result.verification_name} com o valor ${result.verification_value}.`
          : "Domínio removido."),
    );
    if (!error) await load();
  };
  if (!data || !content)
    return (
      <div className="settings-future">
        <strong>A preparar o editor do site…</strong>
        {notice && <p>{notice}</p>}
      </div>
    );
  return (
    <div className="site-builder">
      {notice && (
        <button
          type="button"
          className="stock-notice"
          role="status"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <section className="site-publish-bar">
        <div>
          <strong>
            {data.site.enabled ? "Site publicado" : "Site ainda não publicado"}
          </strong>
          <a href={data.platform_url} target="_blank" rel="noreferrer">
            Abrir site público
          </a>
        </div>
        {canManage && (
          <div>
            <button onClick={() => void openPreview()} disabled={saving}>
              Pré-visualizar
            </button>
            <button onClick={() => void saveDraft()} disabled={saving}>
              Guardar
            </button>
            <button
              className="stock-primary"
              onClick={() => void publish()}
              disabled={saving}
            >
              Publicar
            </button>
          </div>
        )}
      </section>
      <section>
        <header>
          <strong>Template</strong>
          <p>
            Clique num template para o selecionar. A estrutura visual muda,
            mas todo o conteúdo do stand é preservado. Depois pode
            pré-visualizar, guardar ou publicar.
          </p>
        </header>
        <div className="site-template-picker">
          {data.templates.map((item) => (
            <button
              type="button"
              className={templateVersionId === item.version_id ? "active" : ""}
              key={item.version_id}
              disabled={!canManage || saving}
              onClick={() => chooseTemplate(item.version_id)}
            >
              <SiteTemplatePreview config={item.config} name={item.name} />
              <span className="site-template-copy">
                <strong>{item.name}</strong>
                <span>{item.description}</span>
                <small>Versão {item.version_number}</small>
                <b className="site-template-action">
                  {templateVersionId === item.version_id ? "Selecionado" : "Selecionar"}
                </b>
              </span>
            </button>
          ))}
        </div>
      </section>
      <section>
        <header>
          <strong>Identidade e topo</strong>
          <p>Informação principal que o visitante vê ao entrar.</p>
        </header>
        <div className="site-fields">
          <label>
            <span>Nome apresentado</span>
            <small className="field-help">
              Nome do stand mostrado no cabeçalho e no rodapé do site.
            </small>
            <input
              value={content.identity.name}
              onChange={(e) => update("identity", "name", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label>
            <span>Frase da marca</span>
            <small className="field-help">
              Mensagem curta que acompanha o nome e resume o posicionamento do
              stand.
            </small>
            <input
              value={content.identity.tagline}
              onChange={(e) => update("identity", "tagline", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="wide">
            <span>Título principal</span>
            <small className="field-help">
              Primeira mensagem de destaque que o visitante lê ao abrir a página
              inicial.
            </small>
            <input
              value={content.hero.headline}
              onChange={(e) => update("hero", "headline", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="wide">
            <span>Apresentação</span>
            <small className="field-help">
              Texto breve sob o título principal para explicar a proposta do
              stand.
            </small>
            <textarea
              rows={3}
              value={content.hero.intro}
              onChange={(e) => update("hero", "intro", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label>
            <span>Texto do botão</span>
            <small className="field-help">
              Ação principal convidando o visitante a avançar, por exemplo “Ver
              viaturas”.
            </small>
            <input
              value={content.hero.button}
              onChange={(e) => update("hero", "button", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label>
            <span>Cor principal</span>
            <small className="field-help">
              Cor usada nos botões, destaques e elementos ativos do template.
            </small>
            <input
              type="color"
              value={content.colors.accent}
              onChange={(e) => update("colors", "accent", e.target.value)}
              disabled={!canManage}
            />
          </label>
        </div>
      </section>
      <section>
        <header>
          <strong>Pesquisa e partilha</strong>
          <p>Título e descrição usados pelos motores de pesquisa.</p>
        </header>
        <div className="site-fields">
          <label className="wide">
            <span>Título do site</span>
            <small className="field-help">
              Texto apresentado no separador do navegador e nos resultados dos
              motores de pesquisa.
            </small>
            <input
              value={content.seo.title}
              onChange={(e) => update("seo", "title", e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="wide">
            <span>Descrição</span>
            <small className="field-help">
              Resumo que pode aparecer sob o título quando o site é partilhado
              ou encontrado numa pesquisa.
            </small>
            <textarea
              rows={3}
              value={content.seo.description}
              onChange={(e) => update("seo", "description", e.target.value)}
              disabled={!canManage}
            />
          </label>
        </div>
      </section>
      <section>
        <header>
          <strong>Páginas personalizadas</strong>
          <p>
            Construa páginas em blocos. O cabeçalho, rodapé e estilo seguem
            sempre o template escolhido.
          </p>
        </header>
        <div className="site-pages">
          {content.pages.length > 0 && <nav className="site-page-selector" aria-label="Páginas para editar">{content.pages.map((page,index)=><button type="button" key={`${page.slug}-${index}`} className={index === Math.min(activePageIndex,content.pages.length-1) ? "active" : ""} aria-current={index === Math.min(activePageIndex,content.pages.length-1) ? "page" : undefined} onClick={()=>{setActivePageIndex(index);setActiveBlockId(null);setEditorStudioOpen(true)}}><strong>{page.title}</strong><small>/{page.slug}</small></button>)}</nav>}
          {content.pages.map((page, index) => {
            if (index !== Math.min(activePageIndex, content.pages.length - 1)) return null;
            const change = (patch: Partial<Page>) =>
              setContent({
                ...content,
                pages: content.pages.map((x, i) =>
                  i === index ? { ...x, ...patch } : x,
                ),
              });
            const blocks = page.blocks ?? [];
            const activeBlockIndex = blocks.findIndex(block => block.id === activeBlockId);
            const activeBlock = activeBlockIndex >= 0 ? blocks[activeBlockIndex] : undefined;
            const activeBlockLabel = activeBlock ? blockTypes.find(([type]) => type === activeBlock.type)?.[1] ?? "Elemento" : "Elemento";
            const selectedTemplate=data.templates.find(item=>item.version_id===templateVersionId)??data.templates[0];
            const templateConfig=selectedTemplate.config as {layout?:string;colors?:{background?:string;surface?:string;text?:string;accent?:string}};
            const layout=templateConfig.layout??"avenida";
            const editorStand:TemplateStand={slug:"preview",name:content.identity.name||"O seu stand",identityName:content.identity.name||"O seu stand",tagline:content.identity.tagline,headline:content.hero.headline,intro:content.hero.intro,button:content.hero.button,city:"Portugal",pages:content.pages,profile:institutionalProfile,services:publicServices,metrics:publicMetrics,credit:publicCredit,financing:publicFinancing,media:institutionalMedia,reputation:publicReputation,newsletter:publicNewsletter};
            const addBlock=(type:string)=>{const unique=type==="header"||type==="footer";if(unique&&blocks.some(block=>block.type===type))return;const next=type==="header"?[makeBlock(type),...blocks]:type==="footer"?[...blocks,makeBlock(type)]:[...blocks.filter(block=>block.type!=="footer"),makeBlock(type),...blocks.filter(block=>block.type==="footer")];change({blocks:next});setActiveBlockId(next.find(block=>block.type===type&& !blocks.includes(block))?.id??null);setEditorRightTab("block")};
            const moveBlock=(from:number,to:number)=>{if(to<0||to>=blocks.length)return;const next=[...blocks];[next[from],next[to]]=[next[to],next[from]];change({blocks:next})};
            return (
              <article key={`${page.slug}-${index}`}>
                {editorStudioOpen&&<div className={`site-page-studio${editorLeftOpen?" left-open":""}`} style={{"--stand-accent":content.colors.accent||templateConfig.colors?.accent||"#49f5c1","--stand-background":templateConfig.colors?.background||"#fff","--stand-surface":templateConfig.colors?.surface||"#f5f5f5","--stand-text":templateConfig.colors?.text||"#161616"} as CSSProperties}>
                  <header className="site-studio-topbar">
                    <div className="site-studio-history"><button type="button" aria-label="Voltar" onClick={()=>setEditorStudioOpen(false)}>‹</button><button type="button" className={editorLeftOpen?"active":""} aria-label="Adicionar bloco" onClick={()=>setEditorLeftOpen(open=>!open)}>{editorLeftOpen?"×":"+"}</button><button type="button" aria-label="Desfazer">↶</button><button type="button" aria-label="Refazer">↷</button><button type="button" aria-label="Vista em lista">☰</button></div>
                    <div className="site-studio-toolbar"><span>Selecione o texto para o formatar no próprio bloco</span></div>
                    <label className="site-studio-title"><input aria-label="Nome da página" value={page.title} disabled={!canManage} onChange={event=>change({title:event.target.value})}/><span>· Página</span></label>
                    <button type="button" className="site-studio-draft" disabled={saving||!canManage} onClick={()=>void saveDraft()}>Salvar rascunho</button>
                    <button type="button" className="site-studio-publish" disabled={saving||!canManage} onClick={()=>void publish()}>Publicar</button><button type="button" aria-label="Mais opções">⋮</button>
                  </header>
                  {editorLeftOpen&&<aside className="site-studio-left"><header><button className={editorLeftTab==="blocks"?"active":""} onClick={()=>setEditorLeftTab("blocks")}>Blocos</button><button className={editorLeftTab==="patterns"?"active":""} onClick={()=>setEditorLeftTab("patterns")}>Padrões</button><button className={editorLeftTab==="media"?"active":""} onClick={()=>setEditorLeftTab("media")}>Mídia</button><button aria-label="Fechar" onClick={()=>setEditorLeftOpen(false)}>×</button></header><label className="site-studio-search">⌕<input placeholder="Pesquisar" value={editorSearch} onChange={event=>setEditorSearch(event.target.value)}/></label>{editorLeftTab==="patterns"?<nav className="site-studio-patterns">{pagePresets.filter(item=>item.name.toLowerCase().includes(editorSearch.toLowerCase())).map(item=><button type="button" key={item.key} onClick={()=>change({blocks:presetBlocks(item.key)})}><strong>{item.name}</strong><small>{item.description}</small></button>)}</nav>:<div className="site-studio-blocks">{blockTypes.filter(([type,label])=>(editorLeftTab!=="media"||["image","gallery","video"].includes(type))&&label.toLowerCase().includes(editorSearch.toLowerCase())).map(([type,label])=><button type="button" key={type} disabled={(type==="header"||type==="footer")&&blocks.some(block=>block.type===type)} onClick={()=>addBlock(type)}><b aria-hidden="true">{type==="heading"?"T":type==="text"?"¶":type==="image"?"▧":type==="gallery"?"▦":type==="video"?"▶":type==="cta"?"↗":type==="form"?"▤":type==="divider"?"—":type==="columns"?"▥":type==="map"?"⌖":type==="vehicles"?"▱":type==="testimonials"?"❝":type==="faq"?"?":type==="services"?"⌁":type==="header"?"⌃":"⌄"}</b><span>{label}</span></button>)}</div>}</aside>}
                  <div className={`site-studio-canvas-host stand-public-site layout-${layout}`}><DirectPageCanvas blocks={blocks} activeId={activeBlockId} canManage={canManage} organizationId={organizationId} renderHeader={<EditorTemplateHeader layout={layout} stand={editorStand}/>} renderFooter={<ThemeFooter stand={editorStand}/>} onSelect={id=>{setActiveBlockId(id);if(id)setEditorRightTab("block")}} onChange={next=>change({blocks:next})} onOpenLibrary={()=>{setEditorLeftOpen(true);setEditorLeftTab("blocks")}}/></div>
                  <aside className="site-studio-right"><header><button className={editorRightTab==="page"?"active":""} onClick={()=>setEditorRightTab("page")}>Página</button><button className={editorRightTab==="block"?"active":""} onClick={()=>setEditorRightTab("block")}>Bloco</button></header>{editorRightTab==="page"?<div className="site-studio-panel"><strong>{page.title||"Sem título"}</strong><label>Status<select value={page.status??"draft"} disabled={!canManage} onChange={event=>change({status:event.target.value as Page["status"]})}><option value="draft">Rascunho</option><option value="published">Publicada</option><option value="archived">Arquivada</option></select></label><label>Slug<input value={page.slug} disabled={!canManage} onChange={event=>change({slug:event.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")})}/></label><label>Modelo<span>{selectedTemplate.name}</span></label><details><summary>Otimizar SEO</summary><label>Indexação<select value={page.seo?.index===false?"noindex":"index"} onChange={event=>change({seo:{title:page.seo?.title??"",description:page.seo?.description??"",canonical:page.seo?.canonical??"",index:event.target.value==="index"}})}><option value="index">Indexar</option><option value="noindex">Não indexar</option></select></label></details><details><summary>Resumo</summary><textarea value={page.seo?.description??""} onChange={event=>change({seo:{title:page.seo?.title??"",description:event.target.value,canonical:page.seo?.canonical??"",index:page.seo?.index!==false}})}/></details></div>:<div className="site-studio-panel"><strong>{activeBlockLabel}</strong><p>{activeBlock?.type==="header"||activeBlock?.type==="footer"?"Elemento do template, editável e removível nesta página.":"Elemento selecionado na página."}</p>{activeBlock&&<><button disabled={activeBlockIndex===0} onClick={()=>moveBlock(activeBlockIndex,activeBlockIndex-1)}>Mover para cima</button><button disabled={activeBlockIndex===blocks.length-1} onClick={()=>moveBlock(activeBlockIndex,activeBlockIndex+1)}>Mover para baixo</button><button className="danger" onClick={()=>change({blocks:blocks.filter((_,i)=>i!==activeBlockIndex)})}>Remover</button></>}</div>}</aside>
                </div>}
                <details>
                  <summary>Pesquisa e partilha desta página</summary>
                  <div className="site-fields">
                    <label>
                      <span>Título SEO</span>
                      <small className="field-help">
                        Título específico desta página nos resultados de
                        pesquisa; vazio usa o título da página.
                      </small>
                      <input
                        value={page.seo?.title ?? ""}
                        onChange={(e) =>
                          change({
                            seo: {
                              title: e.target.value,
                              description: page.seo?.description ?? "",
                              canonical: page.seo?.canonical ?? "",
                              index: page.seo?.index !== false,
                            },
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Endereço canónico</span>
                      <small className="field-help">
                        Preencha apenas se o conteúdo original existir noutro
                        endereço; evita páginas duplicadas nos motores de
                        pesquisa.
                      </small>
                      <input
                        value={page.seo?.canonical ?? ""}
                        onChange={(e) =>
                          change({
                            seo: {
                              title: page.seo?.title ?? "",
                              description: page.seo?.description ?? "",
                              canonical: e.target.value,
                              index: page.seo?.index !== false,
                            },
                          })
                        }
                      />
                    </label>
                    <label className="wide">
                      <span>Descrição</span>
                      <small className="field-help">
                        Resumo desta página que pode ser apresentado nos
                        resultados de pesquisa.
                      </small>
                      <textarea
                        value={page.seo?.description ?? ""}
                        onChange={(e) =>
                          change({
                            seo: {
                              title: page.seo?.title ?? "",
                              description: e.target.value,
                              canonical: page.seo?.canonical ?? "",
                              index: page.seo?.index !== false,
                            },
                          })
                        }
                      />
                    </label>
                  </div>
                </details>
                {canManage && (
                  <button
                    className="danger"
                    onClick={() => {
                      setActivePageIndex(Math.max(0, index - 1));
                      setContent({
                        ...content,
                        pages: content.pages.filter((_, i) => i !== index),
                      });
                    }}
                  >
                    Arquivar página
                  </button>
                )}
              </article>
            );
          })}
          {canManage && content.pages.length < 30 && (
            <section className="site-page-creator">
              <header><strong>Adicionar página</strong><p>Dê um nome à página e escolha a estrutura inicial. Todos os modelos incluem o cabeçalho e o rodapé do template do site.</p></header>
              <label><span>Nome da página</span><input value={newPageName} placeholder="Ex.: Campanha de verão" onChange={(event)=>setNewPageName(event.target.value)} /></label>
              <fieldset><legend>Modelo da página</legend><div className="site-page-presets">{pagePresets.map(preset=><button type="button" key={preset.key} className={newPagePreset===preset.key?"active":""} aria-pressed={newPagePreset===preset.key} onClick={()=>setNewPagePreset(preset.key)}><strong>{preset.name}</strong><span>{preset.description}</span></button>)}</div></fieldset>
              <button type="button" className="stock-primary site-create-page" disabled={!newPageName.trim()} onClick={()=>{const title=newPageName.trim();const slug=slugifyPageName(title);setActivePageIndex(content.pages.length);setContent({...content,pages:[...content.pages,{...emptyPage,title,slug,editor_version:2,blocks:presetBlocks(newPagePreset),seo:{...emptyPage.seo!}}]});setNewPageName("");setNewPagePreset("basic");}}>Criar página</button>
            </section>
          )}
        </div>
      </section>
      <section>
        <header>
          <strong>Domínio</strong>
          <p>
            O endereço Vorno funciona imediatamente. Um domínio próprio fica
            ativo depois da validação DNS e do certificado SSL.
          </p>
        </header>
        <div className="domain-status">
          <span>Endereço Vorno</span>
          <a href={data.platform_url} target="_blank" rel="noreferrer">
            {data.platform_url}
          </a>
          <b>{data.site.enabled ? "Ativo" : "Aguardando publicação"}</b>
        </div>
        <form className="domain-form" onSubmit={saveDomain}>
          <label>
            <span>Domínio próprio</span>
            <input
              placeholder="www.exemplo.pt"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={!canManage}
            />
          </label>
          <b data-status={data.site.domain_status}>
            {data.site.domain_status.replaceAll("_", " ")}
          </b>
          {canManage && <button disabled={saving}>Guardar</button>}
        </form>
      </section>
      <StandInstitutionalMedia organizationId={organizationId} canManage={canManage} />
      <StandMediaSettings
        organizationId={organizationId}
        canManage={canManage}
      />
      <SiteAdvancedSettings
        organizationId={organizationId}
        canManage={canManage}
        initial={data.site.visual_settings}
      />
      <StandPublicVehicleSettings organizationId={organizationId} canManage={canManage} onChange={setPublicVehicleSettings}/>
      {previewOpen && content && data && (
        <StandSitePreviewModal
          snapshot={previewSnapshot ?? undefined}
          content={
            institutionalProfile?.slogan
              ? {
                  ...content,
                  identity: {
                    ...content.identity,
                    tagline: institutionalProfile.slogan,
                  },
                }
              : content
          }
          profile={institutionalProfile}
          services={publicServices}
          metrics={publicMetrics}
          credit={publicCredit}
          financing={publicFinancing}
          media={institutionalMedia}
          reputation={publicReputation}
          newsletter={publicNewsletter}
          vehicleSettings={publicVehicleSettings}
          template={
            data.templates.find(
              (item) => item.version_id === (previewTemplateVersionId ?? templateVersionId),
            ) ?? data.templates[0]
          }
          standName={content.identity.name || "O seu stand"}
          publishing={saving}
          onClose={() => { setPreviewOpen(false); setPreviewTemplateVersionId(null); }}
          onPublish={(previewTemplateVersionId ?? templateVersionId) === templateVersionId ? () =>
            void publish().then(
              (published) => published && setPreviewOpen(false),
            )
          : undefined}
        />
      )}
    </div>
  );
}
